import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import en from '../i18n/en.json'

interface LanguageContextType {
  language: 'en'
  t: (key: string, params?: Record<string, string | number>) => string
}

const LanguageContext = createContext<LanguageContextType | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Phase 1: always English
  // Phase N: read from user settings, load appropriate locale

  const t = (key: string, params?: Record<string, string | number>): string => {
    // Support flat dot-notation keys like 'nav.dashboard'
    // en.json uses flat structure with dot-notation keys
    const value = (en as Record<string, string>)[key]

    // Fallback: return key itself if not found (prevents crashes)
    let text = typeof value === 'string' ? value : key

    // Support parameter interpolation: {{param}} syntax
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{{${k}}}`, String(v))
      })
    }

    return text
  }

  return (
    <LanguageContext.Provider value={{ language: 'en', t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage(): LanguageContextType {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider')
  return ctx
}
